/* eslint-disable lingui/no-unlocalized-strings, unicorn/no-nested-ternary */
import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import {
  BriefcaseIcon,
  CheckIcon,
  DownloadSimpleIcon,
  LinkSimpleIcon,
  SparkleIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
// These JSON/LinkedIn parsers are separate, lightweight exports unrelated to
// the pdf submodule that parse-file.ts lazy-loads (which pulls in
// pdfjs-dist); Nx's rule can't distinguish sub-module usage within one
// package. The pdf code still code-splits correctly regardless — verified
// via the built `pdf-*.js` chunk.
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  JsonResumeParser,
  LinkedInParser,
  ReactiveResumeParser,
  ReactiveResumeV3Parser,
} from "@reactive-resume/parser";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToastAction,
} from "@reactive-resume/ui";
import { isAxiosError } from "axios";
import { AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import { useAnalytics } from "@/client/hooks/use-analytics";
import { useToast } from "@/client/hooks/use-toast";
import { useImportResume } from "@/client/services/resume/import";
import { parseLinkedinUrl, parseResumeFile } from "@/client/services/resume/parse-file";
import { truncatedParseToast } from "@/client/services/resume/truncated-notice";
import { useDialog } from "@/client/stores/dialog";

import {
  getImportPayload,
  ImportType,
  importTypeOptions,
  type ValidationResult,
} from "./import.utils";

const formSchema = z.object({
  file: z.any(),
  type: z.nativeEnum(ImportType),
});

type FormValues = z.infer<typeof formSchema>;

/**
 * Build a "Detected fields" preview list from the actual parsed import result.
 * Falls back to "—" placeholders for any field the parser couldn't extract;
 * never substitutes a fake sample value.
 */
const buildDetectedFields = (
  validationResult: Extract<ValidationResult, { isValid: true }>,
): [string, string][] => {
  const dash = "—";
  // The parsers return different shapes; defensive read via `any` keeps this
  // hardcoded-mock removal additive — getImportPayload handles full conversion
  // for actual submission.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = validationResult.result as any;
  const basics = raw?.basics ?? raw?.data?.basics ?? {};
  const sections = raw?.sections ?? raw?.data?.sections ?? {};
  const experienceItems = sections.experience?.items ?? raw?.work ?? [];
  const educationItems = sections.education?.items ?? raw?.education ?? [];
  const skillItems = sections.skills?.items ?? raw?.skills ?? [];

  const experienceCount = Array.isArray(experienceItems) ? experienceItems.length : 0;
  const experienceLabel = experienceCount > 0 ? t`${experienceCount} roles detected` : dash;

  const educationLabel =
    Array.isArray(educationItems) && educationItems.length > 0
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        (educationItems[0]?.institution ?? educationItems[0]?.name ?? dash)
      : dash;

  const skillsCount = Array.isArray(skillItems) ? skillItems.length : 0;
  const skillsLabel = skillsCount > 0 ? t`${skillsCount} skills detected` : dash;

  return [
    [t`Name`, typeof basics.name === "string" && basics.name ? basics.name : dash],
    [t`Email`, typeof basics.email === "string" && basics.email ? basics.email : dash],
    [t`Phone`, typeof basics.phone === "string" && basics.phone ? basics.phone : dash],
    [t`Location`, typeof basics.location === "string" && basics.location ? basics.location : dash],
    [t`Experience`, experienceLabel],
    [t`Education`, educationLabel as string],
    [t`Skills detected`, skillsLabel],
  ];
};

// Source cards. Three of the four (From LinkedIn / From a URL / Build with AI)
// are dimmed + click-disabled — the first is rate-limited + LinkedIn-blocked,
// the second was never wired (the json-resume-json type is for file uploads,
// not URL-paste), and the third was a stub that crashed on selection. Only
// "Upload PDF, DOCX, or JSON" is active. Track the others in the backlog so
// they can be brought back when wired.
const sourceCards = [
  {
    type: ImportType["pdf-file"],
    icon: UploadSimpleIcon,
    name: "Upload PDF, DOCX, or JSON",
    desc: "Most accurate · we keep your formatting and pull every section.",
    tone: "bg-indigo-500",
    available: true,
  },
  {
    type: ImportType["linkedin-url"],
    icon: BriefcaseIcon,
    name: "From LinkedIn",
    desc: "Coming soon · LinkedIn-side rate limits make this unreliable today.",
    tone: "bg-[#0A66C2]",
    available: false,
  },
  {
    type: ImportType["json-resume-json"],
    icon: LinkSimpleIcon,
    name: "From a URL",
    desc: "Coming soon · paste a Notion, personal site, or Read.cv link.",
    tone: "bg-zinc-700",
    available: false,
  },
  {
    type: ImportType["docx-file"],
    icon: SparkleIcon,
    name: "Build with AI",
    desc: "Coming soon · answer 6 questions and we draft a full first version.",
    tone: "bg-gradient-to-br from-indigo-500 to-rose-500",
    available: false,
  },
] as const;

export const ImportDialog = () => {
  const { toast } = useToast();
  const { recordJourneyStep } = useAnalytics();
  const { isOpen, close } = useDialog("import");
  const { importResume, loading } = useImportResume();
  const navigate = useNavigate();

  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [linkedinUrlInput, setLinkedinUrlInput] = useState("");

  const form = useForm<FormValues>({
    defaultValues: {
      type: importTypeOptions[0].value,
    },
    resolver: zodResolver(formSchema),
  });
  const filetype = form.watch("type");

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen]);

  useEffect(() => {
    form.reset({ file: undefined, type: filetype });
    setValidationResult(null);
    setLinkedinUrlInput("");
  }, [filetype]);

  const accept = useMemo(() => {
    if (filetype.includes("json")) return ".json";
    if (filetype.includes("zip")) return ".zip";
    if (filetype === ImportType["pdf-file"]) return ".pdf";
    if (filetype === ImportType["docx-file"]) return ".docx";
    return "";
  }, [filetype]);

  const onValidate = async () => {
    if (filetype === ImportType["linkedin-url"]) {
      if (!linkedinUrlInput) {
        toast({ variant: "error", title: t`Please enter a LinkedIn profile URL.` });
        return;
      }
      setIsParsing(true);
      try {
        const result = await parseLinkedinUrl(linkedinUrlInput, {
          onTruncated: () => {
            toast(truncatedParseToast());
          },
        });
        setValidationResult({ isValid: true, type: ImportType["linkedin-url"], result });
      } catch (error) {
        toast({
          variant: "error",
          title: t`Could not import from LinkedIn.`,
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        setIsParsing(false);
      }
      return;
    }

    const { file, type } = form.getValues();

    if (!file) {
      toast({ variant: "error", title: t`Please select a file.` });
      return;
    }

    if (type === ImportType["reactive-resume-json"]) {
      try {
        const parser = new ReactiveResumeParser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);
        setValidationResult({ isValid: true, type, result });
      } catch {
        toast({ variant: "error", title: t`Could not parse the Apply Edge JSON file.` });
      }
    }

    if (type === ImportType["reactive-resume-v3-json"]) {
      try {
        const parser = new ReactiveResumeV3Parser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);
        setValidationResult({ isValid: true, type, result });
      } catch {
        toast({ variant: "error", title: t`Could not parse the legacy v3 JSON file.` });
      }
    }

    if (type === ImportType["json-resume-json"]) {
      try {
        const parser = new JsonResumeParser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);
        setValidationResult({ isValid: true, type, result });
      } catch {
        toast({ variant: "error", title: t`Could not parse the JSON Resume file.` });
      }
    }

    if (type === ImportType["linkedin-data-export-zip"]) {
      try {
        const parser = new LinkedInParser();
        const data = await parser.readFile(file);
        const result = await parser.validate(data);
        setValidationResult({ isValid: true, type, result });
      } catch {
        toast({ variant: "error", title: t`Could not parse the LinkedIn data export.` });
      }
    }

    if (type === ImportType["pdf-file"] || type === ImportType["docx-file"]) {
      setIsParsing(true);
      try {
        const result = await parseResumeFile(file, {
          onTruncated: () => {
            toast(truncatedParseToast());
          },
        });
        setValidationResult({ isValid: true, type, result });
      } catch (error) {
        toast({
          variant: "error",
          title: t`Could not parse the file.`,
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        setIsParsing(false);
      }
    }
  };

  const onImport = async () => {
    if (!validationResult?.isValid) return;

    try {
      const data = getImportPayload(validationResult);
      await importResume({ data });
      close();

      // Map import type to file_type for GA4
      const fileTypeMap: Record<string, string> = {
        [ImportType["pdf-file"]]: "pdf",
        [ImportType["docx-file"]]: "docx",
        [ImportType["reactive-resume-json"]]: "json",
        [ImportType["reactive-resume-v3-json"]]: "json",
        [ImportType["json-resume-json"]]: "json",
        [ImportType["linkedin-data-export-zip"]]: "zip",
        [ImportType["linkedin-url"]]: "linkedin",
      };

      recordJourneyStep("resume_imported", {
        file_type: fileTypeMap[validationResult.type] ?? validationResult.type,
      });
      return;
    } catch (error: unknown) {
      // Surface structured server errors so users can act on them. The
      // generic "Oops, the server returned an error" used to swallow
      // RESUME_LIMIT_REACHED — leaving free-tier users stuck without
      // knowing they needed to upgrade or delete a resume.
      if (isAxiosError(error)) {
        const body = error.response?.data as
          | { code?: string; limit?: number; current?: number; message?: string }
          | undefined;

        if (body?.code === "RESUME_ABSOLUTE_CAP_REACHED") {
          // Hit the safety ceiling on an "unlimited" account — upgrading
          // is meaningless (they already have unlimited). Direct to support.
          toast({
            variant: "error",
            title: t`You've reached the safety cap for your account.`,
            description: t`Please contact support — info@apply-edge.com — to investigate before creating more resumes.`,
          });
          return;
        }

        if (body?.code === "RESUME_LIMIT_REACHED") {
          const limit = body.limit ?? 2;
          toast({
            variant: "error",
            title: t({
              message: `You've used all ${limit} resumes on your current plan.`,
              comment:
                "Shown when a user tries to import a resume but is at their plan's resume limit.",
            }),
            description: t`Delete an existing resume or upgrade to Pro for unlimited resumes.`,
            action: (
              <ToastAction
                altText={t`Upgrade plan`}
                onClick={() => {
                  close();
                  void navigate("/pricing");
                }}
              >
                {t`Upgrade`}
              </ToastAction>
            ),
          });
          return;
        }

        if (body?.message) {
          toast({
            variant: "error",
            title: t`Could not import this resume.`,
            description: body.message,
          });
          return;
        }
      }

      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const onReset = () => {
    form.reset();
    setValidationResult(null);
    setLinkedinUrlInput("");
  };

  const getImportTypeLabel = (label: (typeof importTypeOptions)[number]["label"]) => {
    if (label === "apply-edge-json") return t`Apply Edge (.json)`;
    if (label === "legacy-v3-json") return t`Legacy v3 (.json)`;
    if (label === "json-resume-json") return t`JSON Resume (.json)`;
    if (label === "linkedin-data-export-zip") return t`LinkedIn Data Export (.zip)`;
    if (label === "pdf-file") return t`PDF Resume (.pdf)`;
    return t`Word Resume (.docx)`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="max-w-[720px] overflow-hidden p-0">
        <Form {...form}>
          <form>
            <DialogHeader className="border-b border-border px-7 py-5">
              <DialogTitle className="flex items-start gap-3.5">
                <div className="grid size-10 shrink-0 place-items-center rounded-[11px] bg-primary/10 text-primary">
                  <DownloadSimpleIcon size={18} />
                </div>
                <div>
                  <h2 className="mb-1 text-lg font-semibold">{t`Import a resume`}</h2>
                  <DialogDescription className="text-[13.5px]">
                    {t`PDF, DOCX, LinkedIn, URL, or build from scratch with AI.`}
                  </DialogDescription>
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="px-7 pt-3.5">
              <div className="mb-1 flex items-center gap-0">
                {[t`Source`, t`Confirm fields`, t`Polish`, t`Done`].map((label, index) => (
                  <div key={label} className="flex flex-1 items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div
                        className={[
                          "grid size-[22px] place-items-center rounded-full font-mono text-[11px] font-semibold",
                          validationResult
                            ? index === 0
                              ? "bg-accent text-white"
                              : index === 1
                                ? "bg-primary text-white"
                                : "bg-muted text-muted-foreground"
                            : index === 0
                              ? "bg-primary text-white"
                              : "bg-muted text-muted-foreground",
                        ].join(" ")}
                      >
                        {validationResult && index === 0 ? <CheckIcon size={12} /> : index + 1}
                      </div>
                      <span className="hidden text-[12.5px] font-medium text-muted-foreground sm:block">
                        {label}
                      </span>
                    </div>
                    {index < 3 && <div className="h-0.5 flex-1 bg-border" />}
                  </div>
                ))}
              </div>
            </div>

            <div className="max-h-[min(620px,calc(100vh-220px))] overflow-y-auto px-7 py-5">
              {!validationResult && (
                <>
                  <div className="mb-1 text-[13px] leading-[1.55] text-muted-foreground">
                    {t`Pick where to import from. We'll extract structured fields and let you confirm before saving.`}
                  </div>

                  <div className="mt-3.5 grid gap-2.5 sm:grid-cols-2">
                    {sourceCards.map((source) => {
                      const Icon = source.icon;
                      const selected =
                        source.type === filetype ||
                        (source.type === ImportType["pdf-file"] &&
                          (filetype === ImportType["pdf-file"] ||
                            filetype === ImportType["docx-file"]));
                      const dimmed = !source.available;
                      return (
                        <button
                          key={source.name}
                          type="button"
                          disabled={dimmed}
                          aria-disabled={dimmed}
                          title={dimmed ? "Coming soon" : source.name}
                          className={[
                            "relative flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition",
                            dimmed
                              ? "cursor-not-allowed border-border bg-muted/40 opacity-50"
                              : selected
                                ? "border-primary bg-primary/10 shadow-[0_0_0_2px_hsl(var(--primary)_/_0.12)]"
                                : "border-border bg-background hover:border-primary hover:bg-primary/10",
                          ].join(" ")}
                          onClick={() => {
                            if (dimmed) return;
                            form.setValue("type", source.type);
                          }}
                        >
                          {dimmed ? (
                            <span className="absolute right-3 top-3 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                              Soon
                            </span>
                          ) : null}
                          <span
                            className={`grid size-9 shrink-0 place-items-center rounded-[9px] text-white ${source.tone}`}
                          >
                            <Icon size={16} />
                          </span>
                          <span>
                            <span className="mb-1 block text-sm font-semibold text-foreground">
                              {source.name}
                            </span>
                            <span className="block text-[12.5px] leading-[1.45] text-muted-foreground">
                              {source.desc}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-5">
                    <FormField
                      name="type"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t`Filetype`}</FormLabel>
                          <FormControl>
                            <Select
                              value={field.value}
                              disabled={isParsing}
                              onValueChange={field.onChange}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={t`Please select a file type`} />
                              </SelectTrigger>
                              <SelectContent>
                                {importTypeOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {getImportTypeLabel(option.label)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {filetype === ImportType["linkedin-url"] ? (
                    <FormItem className="mt-4 rounded-xl bg-muted p-[18px]">
                      <FormLabel>{t`LinkedIn Profile URL`}</FormLabel>
                      <FormControl>
                        <Input
                          type="url"
                          placeholder="https://www.linkedin.com/in/username/"
                          disabled={isParsing}
                          value={linkedinUrlInput}
                          onChange={(event) => {
                            setLinkedinUrlInput(event.target.value);
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        {t`Read-only access · we never post on your behalf.`}
                      </FormDescription>
                    </FormItem>
                  ) : (
                    <FormField
                      name="file"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem className="mt-4">
                          <FormLabel>{t`File`}</FormLabel>
                          <FormControl>
                            {/* The whole drop zone is a <label> tied to the
                                hidden file input, so clicking the icon, the
                                copy, or anywhere in the box opens the picker —
                                and files can be dragged in. */}
                            <label
                              htmlFor="resume-file-input"
                              className="flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-border bg-muted p-6 text-center transition hover:border-primary hover:bg-primary/5"
                              onDragOver={(event) => {
                                event.preventDefault();
                              }}
                              onDrop={(event) => {
                                event.preventDefault();
                                if (event.dataTransfer.files.length === 0) return;
                                field.onChange(event.dataTransfer.files[0]);
                              }}
                            >
                              <div className="mb-3 grid size-12 place-items-center rounded-[14px] bg-background text-muted-foreground">
                                <UploadSimpleIcon size={20} />
                              </div>
                              <h4 className="mb-1 mt-0 max-w-full truncate text-sm font-semibold text-foreground">
                                {field.value instanceof File
                                  ? field.value.name
                                  : t`Drop your resume here`}
                              </h4>
                              <p className="text-[13px] text-muted-foreground">
                                {field.value instanceof File
                                  ? t`Click to choose a different file`
                                  : t`Or click to browse · max 10MB`}
                              </p>
                              <input
                                key={`${accept}-${filetype}`}
                                id="resume-file-input"
                                type="file"
                                accept={accept}
                                disabled={isParsing}
                                className="sr-only"
                                onChange={(event) => {
                                  if (!event.target.files?.length) return;
                                  field.onChange(event.target.files[0]);
                                }}
                              />
                              <div className="mt-3 font-mono text-[11px] tracking-[0.06em] text-muted-foreground">
                                PDF · DOCX · TXT
                              </div>
                            </label>
                          </FormControl>
                          <FormMessage />
                          {accept && (
                            <FormDescription>
                              {t({
                                message: `Accepts only ${accept} files`,
                                comment:
                                  "Helper text to let the user know what filetypes are accepted. {accept} can be .pdf or .json.",
                              })}
                            </FormDescription>
                          )}
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}

              {isParsing && (
                <div className="rounded-[14px] border border-primary/20 bg-gradient-to-br from-primary/10 to-background p-8 text-center">
                  <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-4 border-muted border-t-primary" />
                  <h4 className="mb-1.5 mt-0 text-base font-semibold text-foreground">
                    {t`Reading your resume...`}
                  </h4>
                  <p className="m-0 text-[13px] text-muted-foreground">
                    {t`Extracting sections, dates, and keywords. Usually takes 5-10 seconds.`}
                  </p>
                </div>
              )}

              {validationResult !== null && validationResult.isValid && (
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <h4 className="mb-3 mt-0 text-xs font-medium text-muted-foreground">
                      {t`Detected fields`}
                    </h4>
                    <div className="space-y-2">
                      {buildDetectedFields(validationResult).map(([label, value]) => (
                        <div key={label} className="rounded-lg bg-muted px-3 py-2.5">
                          <div className="mb-0.5 text-xs font-medium text-muted-foreground">
                            {label}
                          </div>
                          <div className="text-[13px] text-foreground">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="mb-3 mt-0 text-xs font-medium text-muted-foreground">
                      {t`Source preview`}
                    </h4>
                    <div className="aspect-[8.5/11] w-full rounded-lg bg-zinc-100 p-[14%_12%] shadow-[inset_0_0_0_1px_hsl(var(--border))] dark:bg-zinc-900">
                      <div className="text-lg font-semibold uppercase text-foreground">
                        {buildDetectedFields(validationResult)[0]?.[1] ?? t`Your resume`}
                      </div>
                      <div className="mt-3 h-1.5 w-2/3 rounded bg-foreground/20" />
                      <div className="mt-5 text-xs font-medium text-muted-foreground">
                        Experience
                      </div>
                      <div className="mt-2 h-1.5 w-full rounded bg-foreground/15" />
                      <div className="mt-1.5 h-1.5 w-2/3 rounded bg-foreground/15" />
                      <div className="mt-1.5 h-1.5 w-full rounded bg-foreground/15" />
                      <div className="mt-5 text-xs font-medium text-muted-foreground">Skills</div>
                      <div className="mt-2 h-1.5 w-3/4 rounded bg-foreground/15" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="border-t border-border bg-muted/40 px-7 py-4">
              <AnimatePresence presenceAffectsLayout>
                {!validationResult && (
                  <Button type="button" disabled={isParsing} onClick={onValidate}>
                    {isParsing ? t`Parsing...` : t`Validate`}
                  </Button>
                )}

                {validationResult !== null && validationResult.isValid && (
                  <>
                    <Button type="button" disabled={loading} onClick={onImport}>
                      {t`Import`}
                    </Button>

                    <Button disabled type="button" variant="success">
                      <CheckIcon size={16} weight="bold" className="mr-2" />
                      {t`Validated`}
                    </Button>
                  </>
                )}
              </AnimatePresence>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
